import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLots } from '../../../../contexts/LotsContext';

const LotReviewModal = ({ lot, onClose }) => {
  // Add debug logs at the start
  console.log('=== DEBUG LOT REVIEW MODAL ===');
  console.log('Lot:', lot);
  console.log('Lot quantity:', lot.quantity);
  console.log('Lot movements:', lot.movements);
  console.log('Reconciliation status:', lot.reconciliation_status);
  console.log('===========================');

  const { startLotReview, finishLotReview } = useLots();
  const [initialQuantity, setInitialQuantity] = useState(() => {
    // Add safe checks
    if (lot.reconciliation_status === 'in_progress' && Array.isArray(lot.movements)) {
      const lastReviewStart = lot.movements
        .find(m => m.type === 'quality_check_start');
      return lastReviewStart?.quantity?.toString() || lot.remaining_quantity?.toString() || '0';
    }
    return lot.remaining_quantity?.toString() || '0';
  });
  const [finalQuantity, setFinalQuantity] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewStarted, setReviewStarted] = useState(lot.reconciliation_status === 'in_progress');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);

  // Verificar si el lote ya está siendo repasado
  useEffect(() => {
    if (lot.reconciliation_status === 'in_progress') {
      setReviewStarted(true);
      // Buscar la nota del último movimiento de inicio
      const lastReviewStart = lot.movements
        .find(m => m.type === 'quality_check_start');
      if (lastReviewStart) {
        setNote(lastReviewStart.note);
      }
    }
  }, [lot]);

  const handleStartReview = async () => {
    if (!initialQuantity) return;
    setError(null);

    setIsSubmitting(true);
    try {
      await startLotReview(lot.id, Number(initialQuantity), note);
      setReviewStarted(true);
    } catch (error) {
      console.error('Error al iniciar el repaso:', error);
      setError('No se pudo iniciar el control de calidad. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinishReview = async () => {
    if (!finalQuantity) return;
    setError(null);

    setIsSubmitting(true);
    try {
      await finishLotReview(lot.id, Number(finalQuantity), note);
      onClose();
    } catch (error) {
      console.error('Error al finalizar el repaso:', error);
      setError('No se pudo finalizar el control de calidad. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e, setter) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setter(value);
    }
  };

  // Si el lote ya tiene un repaso pendiente de aprobación, mostrar mensaje
  if (lot.reconciliation_status === 'pending') {
    return (
      <>
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
        <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 z-50 space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Control de Calidad Pendiente
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Este lote ya tiene un control de calidad pendiente de aprobación.
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cerrar
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-800 rounded-t-xl p-4 z-50 space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Control de Calidad
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lot.display_id || lot.id} - {lot.title || lot.name}
            </p>
            {lot.reconciliation_status === 'in_progress' && (
              <p className="text-sm text-yellow-500 dark:text-yellow-400 mt-1">
                Este lote está siendo controlado
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          {!reviewStarted ? (
            // Paso 1: Iniciar repaso
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cantidad a Controlar
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={initialQuantity}
                  onChange={(e) => handleInputChange(e, setInitialQuantity)}
                  className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nota (opcional)
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Agregar una nota..."
                />
              </div>

              <button
                onClick={handleStartReview}
                disabled={!initialQuantity || isSubmitting}
                className="w-full px-4 py-3 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Iniciando...' : 'Iniciar Control'}
              </button>
            </>
          ) : (
            // Paso 2: Finalizar repaso
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cantidad Inicial
                </label>
                <div className="w-full p-3 border dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700 text-center text-lg">
                  {initialQuantity}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Cantidad Final
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={finalQuantity}
                  onChange={(e) => handleInputChange(e, setFinalQuantity)}
                  className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-center text-lg"
                  placeholder="0"
                />
              </div>

              {finalQuantity && (
                <div className={`p-3 rounded-lg ${
                  Number(finalQuantity) < Number(initialQuantity)
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    : Number(finalQuantity) > Number(initialQuantity)
                      ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                      : 'bg-gray-50 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400'
                }`}>
                  <p className="text-center font-medium">
                    {Number(finalQuantity) < Number(initialQuantity) ? 'Baja' : Number(finalQuantity) > Number(initialQuantity) ? 'Rinde' : 'Igual'}: {Number(finalQuantity) - Number(initialQuantity)} {lot.unit}
                    <span className="block text-sm mt-1">
                      ({initialQuantity} → {finalQuantity})
                    </span>
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nota (opcional)
                </label>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="w-full p-3 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  placeholder="Agregar una nota..."
                />
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          {reviewStarted && (
            <button
              onClick={handleFinishReview}
              disabled={!finalQuantity || isSubmitting}
              className="flex-1 px-4 py-3 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Guardando...' : 'Finalizar Control'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default LotReviewModal; 